import { createFileRoute } from '@tanstack/react-router';
import { Tag } from '@osd/icons/tag';
import { User } from '@osd/icons/user';
import { Clock } from '@osd/icons/clock';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';
import { Label } from '@osd/ui/label';
import { Pill } from '@osd/ui/pill';
import { Stack } from '@osd/ui/stack';
import { Table } from '@osd/ui/table';
import { Text } from '@osd/ui/text';

/**
 * /ui/label — live docs for the `Label` primitive.
 *
 * Label is the quiet monochrome tag — a small inline label that
 * doesn't carry semantic color. Use it for "category" or
 * "metadata prefix" annotations next to the thing they describe.
 * For colored chips use `<Pill>`; for form-field labels use
 * `<Field label="…">`.
 */
export const Route = createFileRoute('/ui/_layout/label')({
  component: LabelPage,
});

function LabelPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Label"
        hint="Quiet monochrome inline tag — 2 sizes × optional icon × span / div / p"
      >
        <Text>
          Label has no variant: the whole point is &ldquo;quiet secondary metadata&rdquo;. Color is
          inherited from the surrounding context, so a Label inside an <code>osd-text-muted</code>{' '}
          wrapper inherits muted color automatically.
        </Text>
      </Section>

      <Section title="When to use which?" hint="Label vs Pill vs Badge — decision matrix">
        <Table columns="1fr 2fr 2fr">
          <Table.Header>
            <Table.Row>
              <Table.Head>Primitive</Table.Head>
              <Table.Head>Use when…</Table.Head>
              <Table.Head>Example</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <strong>Label</strong>
              </Table.Cell>
              <Table.Cell>
                Quiet monochrome metadata — categories, prefixes, dates. No semantic color; inherits
                from surrounding context.
              </Table.Cell>
              <Table.Cell>
                <Text>
                  <Label icon={<TagIcon />}>bug</Label> opened by alice 3 days ago
                </Text>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>Pill</strong>
              </Table.Cell>
              <Table.Cell>
                The tag carries semantic intent — status, role, severity. Choose the variant by
                meaning.
              </Table.Cell>
              <Table.Cell>
                <Stack direction="row" gap={1}>
                  <Pill variant="success">active</Pill>
                  <Pill variant="accent" size="sm">
                    admin
                  </Pill>
                </Stack>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>Badge</strong>
              </Table.Cell>
              <Table.Cell>
                Non-semantic numerical count — inbox indicators, notification counters.
              </Table.Cell>
              <Table.Cell>
                <code>&lt;Badge value={'{3}'} /&gt;</code> next to a nav item
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>

      <Section title="Sizes" hint="sm / md">
        <Stack direction="row" gap={2} align="center">
          <Label size="sm">category</Label>
          <Label size="md">category</Label>
        </Stack>
      </Section>

      <Section title="With icon" hint="Leading icon for category labels">
        <Stack direction="row" gap={3} align="center">
          <Label icon={<TagIcon />}>bug</Label>
          <Label icon={<UserIcon />}>alice</Label>
          <Label icon={<ClockIcon />}>3 days ago</Label>
        </Stack>
      </Section>

      <Section
        title="Polymorphic 'as'"
        hint="span (default) / div / p — pick the semantic element that matches the context"
      >
        <Stack gap={3}>
          <Stack gap={2}>
            <Text muted>
              <strong>span</strong> — default. Inline. Fits inside a paragraph or another text run.
            </Text>
            <Text>
              The latest commit <Label icon={<TagIcon />}>bug</Label> was authored by alice 3 hours
              ago.
            </Text>
          </Stack>
          <Stack gap={2}>
            <Text muted>
              <strong>div</strong> — block-level. Sits on its own line. Use above a heading or as a
              metadata header for a section.
            </Text>
            <Label as="div" size="sm">
              CATEGORY
            </Label>
            <h3 style={{ margin: 0 }}>Settings</h3>
          </Stack>
          <Stack gap={2}>
            <Text muted>
              <strong>p</strong> — paragraph-level. Use when the metadata is itself a short sentence
              of context.
            </Text>
            <Label as="p">Last updated 3 days ago by alice.</Label>
          </Stack>
        </Stack>
      </Section>

      <Section
        title="Color inheritance"
        hint="No variant — color comes from the surrounding context"
      >
        <Stack gap={2}>
          <Text>
            <Label>Default (inherits fg)</Label>
          </Text>
          <Text muted>
            <Label>This one is muted (wrapped in osd-text-muted)</Label>
          </Text>
          <Text>
            <span style={{ color: 'var(--osd-color-accent)' }}>
              <Label>This one is accent (wrapped in accent text)</Label>
            </span>
          </Text>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Inline metadata"
            code={`<p>
  <Label icon={<TagIcon />}>bug</Label>
  opened by <Label>alice</Label> 3 days ago
</p>`}
          />
          <CodeBlock
            language="tsx"
            title="Block label above a section"
            code={`<Label as="div" size="sm">CATEGORY</Label>
<h2>Settings</h2>`}
          />
        </Stack>
      </Section>

      <Section title="Accessibility" hint="Per-piece a11y guarantees">
        <Table columns="1fr 2fr 2fr">
          <Table.Header>
            <Table.Row>
              <Table.Head>Element</Table.Head>
              <Table.Head>Guarantee</Table.Head>
              <Table.Head>Notes</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <code>&lt;Label&gt;</code>
              </Table.Cell>
              <Table.Cell>
                Renders as <code>&lt;span&gt;</code>, <code>&lt;div&gt;</code>, or{' '}
                <code>&lt;p&gt;</code> per the <code>as</code> prop
              </Table.Cell>
              <Table.Cell>
                No semantic ARIA role assigned — purely visual. The element choice controls the DOM
                tree shape (inline / block / paragraph) without affecting a11y semantics.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Icon</Table.Cell>
              <Table.Cell>Renders decorative unless given an accessible name</Table.Cell>
              <Table.Cell>
                Icons are rendered without <code>aria-label</code>. If the icon carries meaning,
                wrap in <code>role="img"</code> + <code>aria-label</code> at the call site.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Color</Table.Cell>
              <Table.Cell>No semantic color; not a status indicator</Table.Cell>
              <Table.Cell>
                Label has no variant. If you need a status / severity tag, use{' '}
                <code>&lt;Pill variant="…"&gt;</code> — colourblind users will rely on the label
                text, not the hue.
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>
    </Stack>
  );
}

const TagIcon = Tag;
const UserIcon = User;
const ClockIcon = Clock;
